import React from "react";

interface CSVImportGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CSVImportGuideModal: React.FC<CSVImportGuideModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div 
      className="modal fade show d-block" 
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div 
        className="modal-dialog modal-dialog-centered modal-xl"
        onClick={(e) => e.stopPropagation()}
        style={{ minWidth: "75%", maxWidth: "100%" }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ti ti-file-import me-2"></i>
              CSV Import Instructions
            </h5>
          </div>
          <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="import-guide-content">
              <div className="alert alert-info mb-4">
                <p className="mb-2">
                  <strong>Header Format:</strong> Use <code>company_&#123;field_name&#125;</code> for company fields and <code>contact_&#123;field_name&#125;</code> for contact fields. 
                  Headers are case-insensitive. For <code>custom fields</code> use the same format OR you can copy the field name from 3 dots menu of the custom field.
                </p>
                <p className="mb-0">
                  <strong>File Types:</strong> You can upload data using either an Excel (.xlsx) or a CSV (.csv) file.
                </p>
              </div>

              <section className="mb-4">
                <h5 className="fw-semibold mb-3">Using an Excel File</h5>
                <p>You do not need to format any cells in any special way. Simply enter your data and upload.</p>
              </section>

              <section className="mb-4">
                <h5 className="fw-semibold mb-3">Using a CSV File</h5>
                <div className="alert alert-warning">
                  <p className="mb-2"><strong>Important:</strong> If you use a CSV file, you MUST follow the steps below to prevent data errors.</p>
                  <p className="mb-0">You must format the columns for the following data types as <strong>Text</strong>:</p>
                  <ul className="mb-0 mt-2">
                    <li>Large Number</li>
                    <li>Small Text</li>
                    <li>Large Text</li>
                    <li>Float</li>
                  </ul>
                </div>
                
                <h6 className="fw-semibold mb-3 mt-3">How to Format a Column as Text in CSV:</h6>
                <ul style={{ lineHeight: "2" }}>
                  <li><strong>Step 1:</strong> In your spreadsheet, select the entire column by clicking on its letter at the top (e.g., click on "D" to select all of column D).</li>
                  <li><strong>Step 2:</strong> Right-click on the selected column and choose "Format Cells...".</li>
                  <li><strong>Step 3:</strong> In the pop-up window, under the "Number" tab, select "Text" from the category list.</li>
                  <li><strong>Step 4:</strong> Click "OK".</li>
                  <li><strong>Step 5:</strong> You can now safely enter or paste your data into this column.</li>
                </ul>
              </section>

              <section className="mb-4">
                <h5 className="fw-semibold mb-3">Data Format Reference Table</h5>
                <div className="table-responsive">
                  <table className="table table-striped contact-table">
                    <thead>
                      <tr>
                        <th>Custom Field Type</th>
                        <th>Required Format in Your File</th>
                        <th>Example Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Small Number</strong></td>
                        <td>A whole number between -99,999 and 99,999</td>
                        <td><code>45000</code></td>
                      </tr>
                      <tr>
                        <td><strong>Large Number</strong></td>
                        <td>A whole number (Must format column as Text in CSV)</td>
                        <td><code>12345678998765</code></td>
                      </tr>
                      <tr>
                        <td><strong>Float</strong></td>
                        <td>A number with or without a decimal point (Must format column as Text in CSV)</td>
                        <td><code>1299.95</code></td>
                      </tr>
                      <tr>
                        <td><strong>Small Text</strong></td>
                        <td>Any text up to 255 characters (Must format column as Text in CSV)</td>
                        <td><code>Hello World</code></td>
                      </tr>
                      <tr>
                        <td><strong>Large Text</strong></td>
                        <td>Any text up to 2000 characters (Must format column as Text in CSV)</td>
                        <td><code>Python is a programming language.</code></td>
                      </tr>
                      <tr>
                        <td><strong>Switch</strong></td>
                        <td>TRUE or FALSE</td>
                        <td><code>True</code></td>
                      </tr>
                      <tr>
                        <td><strong>Date</strong></td>
                        <td>A date in YYYY-MM-DD format</td>
                        <td><code>2024-12-31</code></td>
                      </tr>
                      <tr>
                        <td><strong>Time</strong></td>
                        <td>A time in 12-hour format with AM/PM</td>
                        <td><code>05:30:00 PM</code></td>
                      </tr>
                      <tr>
                        <td><strong>Date & Time</strong></td>
                        <td>A date and time in the format YYYY-MM-DD hh:mm:ss AM/PM</td>
                        <td><code>2024-12-31 05:30:00 PM</code></td>
                      </tr>
                      <tr>
                        <td><strong>Dropdown</strong></td>
                        <td>The exact text label of the desired option</td>
                        <td><code>High Priority</code></td>
                      </tr>
                      <tr>
                        <td><strong>Radio Option</strong></td>
                        <td>The exact text label of the desired option</td>
                        <td><code>Email</code></td>
                      </tr>
                      <tr>
                        <td><strong>Multiple Options</strong></td>
                        <td>The text labels of the options, separated by a comma</td>
                        <td><code>Email, Text, Phone Number</code></td>
                      </tr>
                      <tr>
                        <td><strong>Date Range</strong></td>
                        <td>Start and End Date in YYYY-MM-DD format, separated by to</td>
                        <td><code>2024-01-01 to 2024-06-30</code></td>
                      </tr>
                      <tr>
                        <td><strong>Time Range</strong></td>
                        <td>Start and End Time in 12-hour format, separated by to</td>
                        <td><code>09:00:00 AM to 05:00:00 PM</code></td>
                      </tr>
                      <tr>
                        <td><strong>Dependent Field</strong></td>
                        <td>A comma-separated list of LevelName:ChoiceLabel pairs, in the correct dependency order</td>
                        <td><code>Country:Pakistan,Province:Punjab,City:Layyah</code></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-3">
                <h5 className="fw-semibold mb-3">Important Reminders</h5>
                <div className="alert alert-warning" style={{ fontSize: "14px" }}>
                  <ul className="mb-0">
                    <li><strong>Excel files:</strong> No special formatting needed - just enter data and upload</li>
                    <li><strong>CSV files:</strong> Must format Large Number, Small Text, Large Text, and Float columns as Text</li>
                    <li><strong>Text with commas or spaces:</strong> Wrap in double quotes <code>"Company Name, Inc"</code></li>
                    <li><strong>Dropdown/Radio options:</strong> Must exactly match existing options in the system</li>
                    <li><strong>Multiple options:</strong> Separate with commas <code>Email, Text, Phone Number</code></li>
                    <li><strong>Date format:</strong> Use YYYY-MM-DD format <code>2024-12-31</code></li>
                    <li><strong>Time format:</strong> Use 12-hour format with AM/PM <code>05:30:00 PM</code></li>
                    <li><strong>Dependent fields:</strong> Use format <code>LevelName:ChoiceLabel</code> pairs separated by commas</li>
                    <li><strong>Required fields:</strong> Must have values if marked as required in import settings</li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onClose}
            >
              <i className="ti ti-check me-2" />
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVImportGuideModal;

